import { Combobox } from '@noorden-insurtech/alba-design-system';
import { ChevronsUpDown, X } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import type { FilterOption } from '../../hooks/use-reports-filter-options.hook';

/** Campo rotulado dos filtros da Gestão de Carteira. */
export function Field({
  label,
  children,
  className,
}: {
  label: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={`flex flex-col gap-1 ${className ?? ''}`}>
      <label className='text-xs font-semibold text-alba-black-500'>
        {label}
      </label>
      {children}
    </div>
  );
}

/**
 * Combobox dos filtros. O Combobox do Design System não filtra sozinho:
 * mantemos o texto digitado e filtramos os itens (client-side) por label.
 * `inputValue` é controlado para limpar o campo ao selecionar (o rótulo escolhido
 * aparece no Trigger).
 */
export function FilterCombobox({
  placeholder,
  options,
  value,
  onChange,
}: {
  placeholder: string;
  options: FilterOption[];
  value: string;
  onChange: (next: string) => void;
}) {
  const [query, setQuery] = useState('');
  const term = query.trim().toLowerCase();
  const filtered = term
    ? options.filter(option => option.label.toLowerCase().includes(term))
    : options;

  return (
    <Combobox.Root
      items={filtered}
      value={value ? [value] : []}
      inputValue={query}
      onValueChange={details => {
        onChange(details.value[0] || '');
        setQuery('');
      }}
      onInputValueChange={details => setQuery(details.inputValue)}
      selectionBehavior='replace'
    >
      <Combobox.Control className='relative'>
        <Combobox.Trigger className='w-full select-none truncate pr-14 text-left [&_svg]:hidden'>
          {options.find(item => item.value === value)?.label || placeholder}
        </Combobox.Trigger>
        <div className='pointer-events-none absolute right-3 top-1/2 flex -translate-y-1/2 items-center gap-1.5'>
          {value && (
            <button
              type='button'
              aria-label='Limpar'
              onMouseDown={event => event.preventDefault()}
              onClick={event => {
                event.stopPropagation();
                onChange('');
                setQuery('');
              }}
              className='pointer-events-auto flex items-center justify-center rounded p-1 text-alba-gray-600 transition-colors hover:bg-alba-gray-200 hover:text-alba-black-500'
            >
              <X className='h-4 w-4' strokeWidth={1.8} />
            </button>
          )}
          <ChevronsUpDown
            className='h-4 w-4 text-alba-gray-500'
            strokeWidth={1.8}
          />
        </div>
      </Combobox.Control>
      <Combobox.Positioner>
        <Combobox.Content>
          <Combobox.Input placeholder={`${placeholder}...`} />
          <Combobox.List>
            <Combobox.ItemGroup>
              {filtered.map(option => (
                <Combobox.Item key={option.value} item={option}>
                  <Combobox.ItemText>{option.label}</Combobox.ItemText>
                  <Combobox.ItemIndicator />
                </Combobox.Item>
              ))}
            </Combobox.ItemGroup>
            <Combobox.Empty>Nenhum resultado encontrado</Combobox.Empty>
          </Combobox.List>
        </Combobox.Content>
      </Combobox.Positioner>
    </Combobox.Root>
  );
}
